import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pripravBalicek, limitV, trasaVpred, udalostiNaTrase, typyUseku, DOSAH } from './motor';
import { rict, VETA } from './hlas';
import { useOsobniNavigace } from './osobni';

/**
 * Dopravní info v navigaci rozvozce (2026-10-23) — data a upozornění.
 *
 * Balíček (limity, stopky, radary z OSM) si telefon drží v localStorage
 * a stahuje ho jen při nové verzi — navigace tak funguje i bez signálu.
 * Hlášení řidičů se obnovují každých 30 s.
 *
 * Vrací co ukázat (limit, rychlost, nejbližší událost) a sám hlásí hlasem.
 */
const KLIC = 'fl.doprava.balicek';
const OBNOVA_HLASENI_MS = 30000;
const PREKROCENI_MS = 5000;     // tak dlouho nad limitem, než se ozve hlas
const ROZESTUP_RYCHLOST_MS = 60000;

function nactiUlozeny() {
    try { return JSON.parse(localStorage.getItem(KLIC) || 'null'); } catch { return null; }
}

export default function useDopravniInfo({ poloha, routeGeometry, pesiOdKonceM = 0, enabled = true }) {
    const osobni = useOsobniNavigace();
    const [balicek, setBalicek] = useState(() => nactiUlozeny());
    const [nastaveni, setNastaveni] = useState(null);
    const [hlaseni, setHlaseni] = useState([]);

    // Balíček + nastavení
    useEffect(() => {
        if (!enabled) return;
        let zruseno = false;
        const verze = balicek?.verze ?? '';
        fetch(`/api/rozvoz/doprava/balicek?verze=${encodeURIComponent(verze)}`, { credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
                if (zruseno || !d) return;
                setNastaveni(d.nastaveni);
                if (d.data && d.verze) {
                    const novy = { verze: d.verze, data: d.data };
                    setBalicek(novy);
                    try { localStorage.setItem(KLIC, JSON.stringify(novy)); } catch { /* bez úložiště jen v paměti */ }
                }
            })
            .catch(() => {});
        return () => { zruseno = true; };
        // jednou při otevření navigace
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    // Hlášení řidičů
    const nactiHlaseni = useCallback(() => {
        fetch('/api/rozvoz/doprava/hlaseni', { credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => d && setHlaseni(d.hlaseni ?? []))
            .catch(() => {});
    }, []);
    useEffect(() => {
        if (!enabled) return;
        nactiHlaseni();
        const id = setInterval(nactiHlaseni, OBNOVA_HLASENI_MS);
        return () => clearInterval(id);
    }, [enabled, nactiHlaseni]);

    const b = useMemo(() => (balicek?.data ? pripravBalicek(balicek.data) : null), [balicek]);
    const n = nastaveni ?? {};

    // Rychlost a limit
    const rychlostKmh = poloha?.speed != null && poloha.speed >= 0 ? Math.round(poloha.speed * 3.6) : null;
    const naSilnici = useMemo(
        () => (b && poloha?.lat && n.limity !== false ? limitV(b, poloha.lat, poloha.lng, rychlostKmh > 5 ? poloha.heading : null) : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [b, poloha?.lat, poloha?.lng, n.limity],
    );
    const limit = naSilnici?.limit ?? null;
    const tolerance = n.tolerance_kmh ?? 5;
    const prekroceno = limit != null && rychlostKmh != null && rychlostKmh > limit + tolerance;

    // Události na trase
    const trasa = useMemo(() => (b && routeGeometry?.length > 1 ? trasaVpred(b, routeGeometry, 1000) : []), [b, routeGeometry]);
    const udalosti = useMemo(() => udalostiNaTrase(b, trasa, hlaseni, n), [b, trasa, hlaseni, n]);
    const nejblizsi = osobni.upozorneni
        ? udalosti.find((u) => u.s <= (DOSAH[u.typ]?.ukazat ?? 0)) ?? null
        : null;

    // Po čem trasa vede — přerušovaná linka na nezpevněných a pěších úsecích.
    const typy = useMemo(() => typyUseku(b, routeGeometry ?? [], pesiOdKonceM), [b, routeGeometry, pesiOdKonceM]);

    // Hlas: vypnout ho může admin pro všechny, nebo každý sám v ⋯ směny.
    const hlasUdalosti = n.hlas !== false && osobni.hlas && osobni.upozorneni;
    const hlasRychlost = n.hlas !== false && osobni.hlas;

    // ── Hlas ──
    const ohlaseno = useRef(new Set());
    const nadLimitemOd = useRef(null);
    const posledniRychlost = useRef(0);
    const predchoziLimit = useRef(null);

    useEffect(() => {
        if (!enabled || !hlasUdalosti) return;
        for (const u of udalosti) {
            const d = DOSAH[u.typ];
            if (!d || u.s > d.hlas || ohlaseno.current.has(u.id)) continue;
            ohlaseno.current.add(u.id);
            const m = u.typ === 'stopka' || u.typ === 'prednost' ? Math.min(u.s, n.predstih_m ?? 100) : u.s;
            rict(VETA[u.typ](m), u.typ);
        }
    }, [enabled, udalosti, hlasUdalosti, n.predstih_m]);

    useEffect(() => {
        if (!enabled || !hlasRychlost || limit == null) return;
        // Vjezd do nižšího limitu, když jede rychleji
        if (predchoziLimit.current != null && limit < predchoziLimit.current && rychlostKmh != null && rychlostKmh > limit + tolerance) {
            rict(VETA.novyLimit(limit), 'rychlost');
            posledniRychlost.current = Date.now();
        }
        predchoziLimit.current = limit;

        if (!prekroceno) { nadLimitemOd.current = null; return; }
        nadLimitemOd.current ??= Date.now();
        if (Date.now() - nadLimitemOd.current >= PREKROCENI_MS && Date.now() - posledniRychlost.current >= ROZESTUP_RYCHLOST_MS) {
            posledniRychlost.current = Date.now();
            rict(VETA.rychle(rychlostKmh, limit), 'rychlost');
        }
    }, [enabled, limit, rychlostKmh, prekroceno, tolerance, hlasRychlost]);

    // Ohlášené události zapomenout, když už nejsou na trase (projel je).
    useEffect(() => {
        const ted = new Set(udalosti.map((u) => u.id));
        for (const id of ohlaseno.current) if (!ted.has(id)) ohlaseno.current.delete(id);
    }, [udalosti]);

    // ── Hlášení ──
    const nahlasit = useCallback(async (typ) => {
        if (!poloha?.lat) throw new Error('Chybí poloha.');
        const r = await fetch('/api/rozvoz/doprava/hlaseni', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ typ, lat: poloha.lat, lng: poloha.lng }),
        });
        if (!r.ok) {
            const d = await r.json().catch(() => ({}));
            throw new Error(d.message || 'Hlášení se nepodařilo uložit.');
        }
        nactiHlaseni();
    }, [poloha?.lat, poloha?.lng, nactiHlaseni]);

    return {
        pripraveno: !!b,
        nastaveni: n,
        limit: n.limity === false ? null : limit,
        limitOdvozeny: naSilnici?.odvozeny ?? false,
        rychlostKmh,
        prekroceno,
        nejblizsi,
        typyUseku: typy,
        hlaseni,
        radary: b && n.radary !== false ? b.radary : [],
        nahlasit,
        hlaseniZapnuta: n.hlaseni !== false,
    };
}
